'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@mentora/ui-components'
import { useTranslations } from 'next-intl'
import { PercentRateForm } from './percent-rate-form'

/** 100% in basis points — mirrors `MAX_PLATFORM_FEE_BPS`, which this client file cannot import. */
const MAX_RATE_BPS = 10_000

export interface AdminSettingsPanelActions {
  updateInstallmentRate: (input: { rateBps: number }) => Promise<ActionResult>
  updatePlatformFeeRate: (input: { rateBps: number }) => Promise<ActionResult>
}

export interface AdminSettingsPanelProps {
  installmentMonthlyRateBps: number
  platformFeeBps: number
  /** The storefront's own server actions — the one thing the two copies of this screen never shared. */
  actions: AdminSettingsPanelActions
}

export function AdminSettingsPanel({ installmentMonthlyRateBps, platformFeeBps, actions }: AdminSettingsPanelProps) {
  const t = useTranslations('adminSettings')

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-8 sm:px-8">
        <div>
          <h1 className="font-(--font-display) text-3xl tracking-tight text-foreground">
            {t('title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('subtitle')}</p>
        </div>

        <Tabs defaultValue="taxes">
          <TabsList>
            <TabsTrigger value="taxes">{t('tabs.taxes')}</TabsTrigger>
            <TabsTrigger value="transactions">{t('tabs.transactions')}</TabsTrigger>
          </TabsList>

          {/* ── Taxes ───────────────────────────────────────── */}
          <TabsContent value="taxes" className="mt-4">
            <div className="flex flex-col gap-4">
              <PercentRateForm
                id="platformFeeRate"
                rateBps={platformFeeBps}
                maxBps={MAX_RATE_BPS}
                save={actions.updatePlatformFeeRate}
                copy={{
                  title: t('taxes.platformFee.title'),
                  subtitle: t('taxes.platformFee.subtitle'),
                  label: t('taxes.platformFee.label'),
                  suffix: t('taxes.platformFee.suffix'),
                  hint: t('taxes.platformFee.hint'),
                  submit: t('taxes.platformFee.submit'),
                  submitting: t('taxes.platformFee.submitting'),
                  invalid: t('taxes.errors.invalidRate'),
                  saved: t('taxes.toasts.saved'),
                }}
              />
              <PercentRateForm
                id="installmentRate"
                rateBps={installmentMonthlyRateBps}
                maxBps={MAX_RATE_BPS}
                save={actions.updateInstallmentRate}
                copy={{
                  title: t('taxes.installmentRate.title'),
                  subtitle: t('taxes.installmentRate.subtitle'),
                  label: t('taxes.installmentRate.label'),
                  suffix: t('taxes.installmentRate.suffix'),
                  hint: t('taxes.installmentRate.hint'),
                  submit: t('taxes.installmentRate.submit'),
                  submitting: t('taxes.installmentRate.submitting'),
                  invalid: t('taxes.errors.invalidRate'),
                  saved: t('taxes.toasts.saved'),
                }}
              />
            </div>
          </TabsContent>

          {/* ── Transactions (placeholder) ──────────────────── */}
          <TabsContent value="transactions" className="mt-4">
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border/70 py-12 text-center">
              <p className="font-medium text-foreground">
                {t('transactions.emptyTitle')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('transactions.emptySubtitle')}
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
